import { Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Permission } from '@valiosascrm/shared';
import { useAuthStore } from '@/stores/auth';
import { useCanAccess } from '@/hooks/useCanAccess';
import { DashboardLayout } from './layouts/DashboardLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { Empty, Button } from './design-system/primitives';
import { WhatsAppGated } from './components/WhatsAppGated';
import { lazyRoute } from '@/lib/lazy-route';
// Story 65.45: a tela de login vem junto com a aplicação. É para ela que a sessão que cai leva,
// e um pedaço que não carrega ali recarregaria a página e apagaria o texto guardado.
import Login from './pages/auth/Login';

/**
 * Sober DS route tree — v2 is now mounted at canonical root paths in App.tsx.
 * Reuses the same providers/RouteGuard and keeps legacy /v2 URLs as redirects
 * at the app shell boundary.
 */
const TokensProof = lazyRoute(() => import('./pages/TokensProof'));
const CatalogProof = lazyRoute(() => import('./pages/CatalogProof'));
const OverlaysProof = lazyRoute(() => import('./pages/OverlaysProof'));
const Dashboard = lazyRoute(() => import('./pages/Dashboard'));
const Clinicas = lazyRoute(() => import('./pages/Clinicas'));
const Inbox = lazyRoute(() => import('./pages/Inbox'));
const Crm = lazyRoute(() => import('./pages/Crm'));
const Pacientes = lazyRoute(() => import('./pages/Pacientes'));
const Agendamentos = lazyRoute(() => import('./pages/Agendamentos'));
const Financeiro = lazyRoute(() => import('./pages/Financeiro'));
const Analytics = lazyRoute(() => import('./pages/Analytics'));
const Estoque = lazyRoute(() => import('./pages/Estoque'));
const Relatorios = lazyRoute(() => import('./pages/Relatorios'));
const PrevisaoChurn = lazyRoute(() => import('./pages/PrevisaoChurn'));
const PrevisaoReceita = lazyRoute(() => import('./pages/PrevisaoReceita'));
const Campanhas = lazyRoute(() => import('./pages/Campanhas'));
const CampanhaDetalhe = lazyRoute(() => import('./pages/CampanhaDetalhe'));
const Pricing = lazyRoute(() => import('./pages/Pricing'));
const Billing = lazyRoute(() => import('./pages/Billing'));
const Menu = lazyRoute(() => import('./pages/Menu'));
const PacienteDetalhe = lazyRoute(() => import('./pages/PacienteDetalhe'));
const Anamnese = lazyRoute(() => import('./pages/Anamnese'));
const Unidades = lazyRoute(() => import('./pages/Unidades'));
const OrganizarUnidades = lazyRoute(() => import('./pages/OrganizarUnidades'));
const Webhooks = lazyRoute(() => import('./pages/Webhooks'));
const WhatsApp = lazyRoute(() => import('./pages/WhatsApp'));
const PrimeirosPassos = lazyRoute(() => import('./pages/PrimeirosPassos'));
const ConfiguracaoRapida = lazyRoute(() => import('./pages/ConfiguracaoRapida'));
const Ajuda = lazyRoute(() => import('./pages/Ajuda'));
const PublicPricingPage = lazyRoute(() => import('./pages/PublicPages'), (m) => m.PublicPricingPage);
const PublicTermsPage = lazyRoute(() => import('./pages/PublicPages'), (m) => m.PublicTermsPage);
const PublicPrivacyPage = lazyRoute(() => import('./pages/PublicPages'), (m) => m.PublicPrivacyPage);
const PublicLgpdPage = lazyRoute(() => import('./pages/PublicPages'), (m) => m.PublicLgpdPage);
const Register = lazyRoute(() => import('./pages/auth/Register'));
const ForgotPassword = lazyRoute(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazyRoute(() => import('./pages/auth/ResetPassword'));
const BusinessOnboarding = lazyRoute(() => import('./pages/auth/BusinessOnboarding'));
const AcceptInvite = lazyRoute(() => import('./pages/auth/AcceptInvite'));
const Landing = lazyRoute(() => import('./pages/Landing'));
const Configuracoes = lazyRoute(() => import('./pages/Configuracoes'));
const ConfigClinica = lazyRoute(() => import('./pages/ConfigClinica'));
const ConfigServicos = lazyRoute(() => import('./pages/ConfigServicos'));
const ConfigPacotes = lazyRoute(() => import('./pages/ConfigPacotes'));
const ConfigPerfil = lazyRoute(() => import('./pages/ConfigPerfil'));
const ConfigEquipe = lazyRoute(() => import('./pages/ConfigEquipe'));
const ConfigComissoes = lazyRoute(() => import('./pages/ConfigComissoes'));
const ConfigTaxas = lazyRoute(() => import('./pages/ConfigTaxas'));
const ConfigRespostas = lazyRoute(() => import('./pages/ConfigRespostas'));
const ConfigMensagens = lazyRoute(() => import('./pages/ConfigMensagens'));
const ConfigLgpd = lazyRoute(() => import('./pages/ConfigLgpd'));
const ConfigModelosDocumento = lazyRoute(() => import('./pages/ConfigModelosDocumento'));
const ConfigIntegracoes = lazyRoute(() => import('./pages/ConfigIntegracoes'));
const Mensagens = lazyRoute(() => import('./pages/Mensagens'));
const Resultados = lazyRoute(() => import('./pages/Resultados'));

function V2Loader() {
  return (
    <div
      data-ui="v2"
      className="v2-root flex min-h-dvh-safe items-center justify-center"
      role="status"
      aria-label="Carregando"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-sober-primary border-t-transparent" />
    </div>
  );
}

function ComingSoon() {
  return (
    <Empty
      title="Em construção"
      description="Esta tela do Sober DS chega numa próxima fase da migração (P1+)."
      action={
        <Button variant="secondary" asChild>
          <a href="/dashboard">Voltar ao dashboard</a>
        </Button>
      }
    />
  );
}

/**
 * Story 27.31 (cutover) — auth gate for the v2 app shell; unauth → /login.
 * Story 43.1 (RC-025) — /ajuda é rota ÚNICA session-aware: logada renderiza dentro
 * do DashboardLayout (mesma instância do shell — GuidedTour montado, sem remount);
 * deslogada renderiza o Outlet cru e a página decide o PublicShell pela sessão.
 */
export function V2Shell() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();
  if (isAuthenticated) return <DashboardLayout />;
  if (pathname === '/ajuda') return <Outlet />;
  return <Navigate to="/login" replace />;
}

/**
 * Story 27.31 — route-level permission redirect (v1 parity, AC6). The Sidebar
 * already hides unauthorized nav and the API enforces server-side; this adds the
 * v1-style redirect for direct-URL access. Reuses useCanAccess (engine).
 */
export function Perm({ p, children }: { p: Permission; children: ReactNode }) {
  const allowed = useCanAccess(p);
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export function V2Routes() {
  return (
    <Suspense fallback={<V2Loader />}>
      <Routes>
        {/* Public marketing landing — standalone (no shell). */}
        <Route index element={<Landing />} />
        <Route path="landing" element={<Landing />} />
        <Route path="precos" element={<PublicPricingPage />} />
        <Route path="termos" element={<PublicTermsPage />} />
        <Route path="privacidade" element={<PublicPrivacyPage />} />
        <Route path="lgpd" element={<PublicLgpdPage />} />

        {/* Foundation proof pages — standalone (no shell) */}
        <Route path="tokens" element={<TokensProof />} />
        <Route path="catalog" element={<CatalogProof />} />
        <Route path="overlays" element={<OverlaysProof />} />

        {/* Auth (AuthLayout) — Story 27.29 */}
        <Route element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="onboarding-empresa" element={<BusinessOnboarding />} />
          <Route path="accept-invite" element={<AcceptInvite />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
        </Route>

        {/* App shell (DashboardLayout) — auth-gated (Story 27.31 cutover).
            Per-route Perm mirrors the v1 permission map (AC6). */}
        <Route element={<V2Shell />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clinicas" element={<Clinicas />} />
          <Route
            path="inbox"
            element={
              <Perm p="inbox:read">
                <WhatsAppGated>
                  <Inbox />
                </WhatsAppGated>
              </Perm>
            }
          />
          <Route
            path="crm"
            element={
              <Perm p="leads:read">
                <Crm />
              </Perm>
            }
          />
          <Route
            path="pacientes"
            element={
              <Perm p="patients:read">
                <Pacientes />
              </Perm>
            }
          />
          <Route
            path="pacientes/:id"
            element={
              <Perm p="patients:read">
                <PacienteDetalhe />
              </Perm>
            }
          />
          <Route
            path="pacientes/:id/anamnese"
            element={
              // Story 37.2 — a página de anamnese SÓ preenche (POST /responses exige anamnesis:fill).
              // Gate com patients:read deixava a secretária entrar e perder tudo no 403.
              <Perm p="anamnesis:fill">
                <Anamnese />
              </Perm>
            }
          />
          <Route
            path="unidades"
            element={
              <Perm p="locations:manage">
                <Unidades />
              </Perm>
            }
          />
          <Route
            path="unidades/organizar"
            element={
              <Perm p="locations:manage">
                <OrganizarUnidades />
              </Perm>
            }
          />
          <Route
            path="primeiros-passos"
            element={
              <Perm p="settings:manage">
                <PrimeirosPassos />
              </Perm>
            }
          />
          {/* Story 70.7 — Configuração rápida, rota provisória e sem link (RC-066) até a 70.27
              trocar o Primeiros passos. Só a administradora (RC-170). */}
          <Route
            path="configuracao-rapida"
            element={
              <Perm p="settings:manage">
                <ConfiguracaoRapida />
              </Perm>
            }
          />
          <Route path="ajuda" element={<Ajuda />} />
          {/* Classe A: o hub não tem gate próprio — cada SettingsTile já se esconde
              por permissão (useCanAccess). Gate no hub escondia telas que a role
              PODE usar, e o redirect mudo lia como bug. */}
          <Route path="config" element={<Configuracoes />} />
          <Route path="mensagens" element={<Mensagens />} />
          <Route
            path="config/clinica"
            element={
              <Perm p="settings:manage">
                <ConfigClinica />
              </Perm>
            }
          />
          {/* Catálogo: quem escreve serviço no backend (services:write) alcança a tela. */}
          <Route
            path="config/servicos"
            element={
              <Perm p="services:write">
                <ConfigServicos />
              </Perm>
            }
          />
          <Route
            path="config/pacotes"
            element={
              <Perm p="services:write">
                <ConfigPacotes />
              </Perm>
            }
          />
          {/* Onda B-2 (E3): tela absorvida pela Equipe unificada — deep-links/tour/onboarding preservados. */}
          <Route path="config/profissionais" element={<Navigate to="/config/equipe" replace />} />
          <Route path="config/perfil" element={<ConfigPerfil />} />
          <Route
            path="config/equipe"
            element={
              <Perm p="settings:view">
                <ConfigEquipe />
              </Perm>
            }
          />
          {/* Comissão é dinheiro, não configuração — gate no domínio. A secretária
              segue de fora (não tem commissions:read); o que muda é o MOTIVO. */}
          <Route
            path="config/comissoes"
            element={
              <Perm p="commissions:read">
                <ConfigComissoes />
              </Perm>
            }
          />
          {/* Taxa da maquininha é operação de caixa: quem cobra precisa ler. */}
          <Route
            path="config/taxas"
            element={
              <Perm p="financial:read">
                <ConfigTaxas />
              </Perm>
            }
          />
          {/* Respostas rápidas são do INBOX, não de message-templates: a API é
              quick-replies (inbox:read/write/manage). Gate espelha o backend real. */}
          <Route
            path="config/respostas"
            element={
              <Perm p="inbox:write">
                <ConfigRespostas />
              </Perm>
            }
          />
          <Route
            path="config/mensagens"
            element={
              <Perm p="message-templates:update">
                <ConfigMensagens />
              </Perm>
            }
          />
          <Route
            path="config/lgpd"
            element={
              <Perm p="settings:manage">
                <ConfigLgpd />
              </Perm>
            }
          />
          <Route
            path="config/modelos-documento"
            element={
              <Perm p="settings:manage">
                <ConfigModelosDocumento />
              </Perm>
            }
          />
          <Route
            path="config/webhooks"
            element={
              <Perm p="webhooks:manage">
                <Webhooks />
              </Perm>
            }
          />
          <Route
            path="config/avancado/integracoes"
            element={
              <Perm p="webhooks:manage">
                <ConfigIntegracoes />
              </Perm>
            }
          />
          <Route
            path="config/whatsapp"
            element={
              <Perm p="whatsapp:manage">
                <WhatsAppGated>
                  <WhatsApp />
                </WhatsAppGated>
              </Perm>
            }
          />
          <Route
            path="agendamentos"
            element={
              <Perm p="appointments:read">
                <Agendamentos />
              </Perm>
            }
          />
          <Route
            path="financeiro"
            element={
              <Perm p="financial:read">
                <Financeiro />
              </Perm>
            }
          />
          <Route
            path="analytics"
            element={
              <Perm p="analytics:view">
                <Analytics />
              </Perm>
            }
          />
          <Route path="resultados" element={<Resultados />} />
          <Route
            path="estoque"
            element={
              <Perm p="inventory:read">
                <Estoque />
              </Perm>
            }
          />
          <Route
            path="relatorios"
            element={
              <Perm p="reports:view">
                <Relatorios />
              </Perm>
            }
          />
          <Route
            path="ai/churn"
            element={
              <Perm p="ai:view">
                <PrevisaoChurn />
              </Perm>
            }
          />
          <Route
            path="ai/forecast"
            element={
              <Perm p="ai:view">
                <PrevisaoReceita />
              </Perm>
            }
          />
          <Route
            path="campanhas"
            element={
              <Perm p="campaigns:read">
                <Campanhas />
              </Perm>
            }
          />
          <Route
            path="campanhas/:id"
            element={
              <Perm p="campaigns:read">
                <CampanhaDetalhe />
              </Perm>
            }
          />
          <Route
            path="pricing"
            element={
              <Perm p="billing:view">
                <Pricing />
              </Perm>
            }
          />
          <Route
            path="billing"
            element={
              <Perm p="billing:view">
                <Billing />
              </Perm>
            }
          />
          <Route path="menu" element={<Menu />} />
          <Route path="*" element={<ComingSoon />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default V2Routes;
